import { Box, Button, Fade, Typography, useTheme } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { tabs } from "./data";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TabBar from "./tabs/booking-details/components/TabBar";
import BookingDetails from "./tabs/booking-details/BookingDetails";
import ProposalTab from "./tabs/proposals/Proposals";
import { useBookingData } from "./hooks/UseBookingData";

import BookingAudit from "./tabs/audit/BookingAudit";
import MemoryTableTab from "./tabs/memory-table/MemoryTableTab";
import ClientReviewTab from "./tabs/client-review/ClientReview";
import { tokens } from "../../../theme";

const isFuneralBooking = (bookingType?: string | null) => {
  const type = bookingType?.toUpperCase();
  return !type || type === "FUNERAL" || type === "FUNERAL_HOME";
};

const TAB_SLUGS = [
  "booking",
  "proposal",
  "memory-table",
  "client-review",
  "audit",
];

export default function ViewBookingPage2() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = useMemo(() => {
    const index = TAB_SLUGS.indexOf(searchParams.get("tab") ?? "");
    return index === -1 ? 0 : index;
  }, [searchParams]);

  const [activeTab, setActiveTab] = useState(initialTab);
  const { bookingData } = useBookingData();

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    setSearchParams(
      (prev) => {
        prev.set("tab", TAB_SLUGS[index]);
        return prev;
      },
      { replace: true },
    );
  };

  const tabLabels = tabs.map((label, index) =>
    index === 2 && !isFuneralBooking(bookingData?.booking_type)
      ? "Comments"
      : label,
  );

  return (
    <Box sx={{ px: { xs: 1, sm: 2 } }}>
      <Button
        onClick={() => navigate(-1)}
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        sx={{
          minWidth: "auto",
          px: 2,
          py: 1.1,
          mb: 2,
          borderRadius: "999px",
          bgcolor: colors.primary[800],
          color: colors.primary[400],
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",
          border: `1px solid ${colors.primary[400]}`,
          "&:hover": { borderColor: `${colors.primary[400]}44` },
        }}
      >
        BACK
      </Button>

      <TabBar tabs={tabLabels} activeIndex={activeTab} onChange={handleTabChange} />

      <Fade in={activeTab === 0} unmountOnExit={false}>
        <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
          <BookingDetails />
        </Box>
      </Fade>
      <Fade in={activeTab === 1} unmountOnExit={false}>
        <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
          <ProposalTab bookingData={bookingData} />
        </Box>
      </Fade>
      <Fade in={activeTab === 2} unmountOnExit={false}>
        <Box sx={{ display: activeTab === 2 ? "block" : "none" }}>
          <MemoryTableTab bookingData={bookingData} />
        </Box>
      </Fade>
      <Fade in={activeTab === 3} unmountOnExit={false}>
        <Box sx={{ display: activeTab === 3 ? "block" : "none" }}>
          {bookingData ? (
            <ClientReviewTab bookingId={bookingData.id.toString()} />
          ) : (
            <Typography>Loading review...</Typography>
          )}
        </Box>
      </Fade>
      <Fade in={activeTab === 4} unmountOnExit={false}>
        <Box sx={{ display: activeTab === 4 ? "block" : "none" }}>
          {bookingData ? (
            <BookingAudit bookingId={bookingData.id.toString()} />
          ) : (
            <Typography>Loading audit history...</Typography>
          )}
        </Box>
      </Fade>
    </Box>
  );
}
